/**
 * Shared list of an account's hardware orders — status, price, delivery
 * address, and tracking. Rendered both on the Order page and in Settings so a
 * customer can find "where's my button?" without going through the buy flow.
 */

import type { OrderWithId } from '@/hooks/useOrders';
import type { Order } from '@/schemas';
import { BUTTON_PRICE_GBP } from '@/lib/pricing';

const STATUS_TEXT: Record<Order['status'], string> = {
  paid: 'Order confirmed — preparing your button',
  preparing: 'Preparing your button',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
};

function formatGbp(pence: number | null | undefined, fallbackPounds: number): string {
  if (pence != null) return `£${(pence / 100).toFixed(2)}`;
  return `£${fallbackPounds}`;
}

function formatDate(ts: { toDate(): Date } | undefined): string {
  if (!ts) return '';
  try {
    return ts.toDate().toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

export function OrdersList({
  orders,
  elderName,
}: {
  orders: OrderWithId[];
  elderName?: string;
}) {
  return (
    <ul className="flex flex-col gap-3">
      {orders.map((order) => (
        <li
          key={order.id}
          className="flex flex-col gap-1 rounded-card border border-paper-border bg-white px-4 py-3 text-sm"
        >
          <div className="flex items-baseline justify-between">
            <span className="font-semibold text-ink">
              {order.quantity} {order.quantity === 1 ? 'button' : 'buttons'}
              {elderName ? ` for ${elderName}` : ''}
            </span>
            <span className="text-ink-soft">
              {formatGbp(order.amountTotal, order.quantity * BUTTON_PRICE_GBP)}
            </span>
          </div>
          <div className="text-ink-soft">{STATUS_TEXT[order.status]}</div>
          {order.status === 'shipped' && order.trackingUrl && (
            <a
              href={order.trackingUrl}
              target="_blank"
              rel="noreferrer"
              className="text-accent-700 hover:underline"
            >
              Track parcel
              {order.carrier ? ` (${order.carrier})` : ''} →
            </a>
          )}
          {order.shippingAddress && (
            <div className="text-xs text-ink-muted">
              Shipping to:{' '}
              {[
                order.shippingAddress.name,
                order.shippingAddress.line1,
                order.shippingAddress.line2,
                order.shippingAddress.city,
                order.shippingAddress.postalCode,
              ]
                .filter(Boolean)
                .join(', ')}
            </div>
          )}
          {order.createdAt && (
            <div className="text-xs text-ink-muted">
              Ordered {formatDate(order.createdAt)}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}
